import React, { useEffect, useMemo, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import styles from './ScheduleForm.styles';
import services from 'services';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/dist/ie11/yup';
import { Controller, useForm } from 'react-hook-form/dist/index.ie11';
import Button from '@mui/material/Button';
import Progress from 'studio/components/Progress';
import Select from 'studio/components/Select';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import { sortBy } from 'utils/arrayUtils';
import { useLoader } from 'components/loaders/FadeLoader';
import { useMessage } from 'studio/components/Message';
import Synchronizable, { getLabel } from 'studio/pages/Synchronization/Synchronizable/Synchronizable';
import _ from 'lodash';
import { trackEvent } from 'utils/eventTracking';
import { allConceptsText, getSynchronizableConcepts, syncPolicies } from 'studio/pages/Synchronization/Synchronization';
import FormControlLabel from '@mui/material/FormControlLabel';
import { vklTypes } from 'studio/pages/Editor/VklSyntaxHighlighter';

// the validation rules for the form
const schema = yup.object().shape({
  startDate: yup
    .date()
    .required(' ')
    .typeError('Invalid start date or time')
    .test('start-date-future', 'The start date and time must be in the future', value => value > new Date()),
  frequency: yup
    .string()
    // disallow the 'custom' frequency
    .oneOf(['once', 'hourly', 'daily', 'weekly'])
    .required(),
  // don't specifiy the type '.of(...)' since if specified, then the 'min' requirement doesn't work
  synchronizables: yup.array().min(1, 'You need to select at least one concept').nullable()
});

const ScheduleForm = ({ classes, knowledgeGraph, schedule, onCancel, onSuccess }) => {
  const { withLoader, loading } = useLoader();
  const { successMessage, errorMessage } = useMessage();

  // the synchronizables concepts that can be selected for synchronization
  const [synchronizableConcepts, setSynchronizableConcepts] = useState(null);

  // the ID of the knowledge graph
  const kgId = knowledgeGraph?.data.id;
  // the initial start date to pre-select in the date and time picker:
  // set it to the next hour with a buffer of 10 minutes
  const initialStartDate = new Date();
  initialStartDate.setMinutes(initialStartDate.getMinutes() + 10);
  initialStartDate.setHours(initialStartDate.getHours() + 1);
  initialStartDate.setMinutes(0, 0, 0);

  const { handleSubmit, control, errors, watch, setValue, formState, trigger } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: useMemo(
      () => ({
        startDate: schedule ? new Date(schedule.data.startDate) : null,
        frequency: schedule ? schedule.data.frequency : 'once',
        synchronizables: schedule ? schedule.data.synchronizables || null : []
      }),
      [schedule]
    )
  });

  const data = watch();

  // converts the specified synchronizable concept to a synchronizable object
  const toSynchronizable = synchronizableConcept => {
    let vklType = null;
    synchronizableConcept.type === 'Class' && (vklType = vklTypes.class);
    synchronizableConcept.type === 'CoOccurrence' && (vklType = vklTypes.object);
    return {
      uri: synchronizableConcept.data.uri,
      vklQualifiedIdentifier: synchronizableConcept.data.vklQualifiedIdentifier,
      vklType,
      syncPolicy: synchronizableConcept.data.syncPolicy
    };
  };

  // the synchronizables corresponding to the synchronizable concepts, sorted by their label
  const synchronizables = synchronizableConcepts && sortBy(synchronizableConcepts.map(toSynchronizable), getLabel);

  // loads all synchronizable concepts
  const loadSynchronizableConcepts = async () => {
    const synchronizableConcepts = await withLoader(getSynchronizableConcepts(kgId));
    setSynchronizableConcepts(synchronizableConcepts);
  };

  const createSynchronizationSchedule = async (startDate, frequency, synchronizables) => {
    const schedule = {
      startDate,
      frequency,
      synchronizables
    };
    const result = await withLoader(services.createSynchronizationSchedule(kgId, schedule));
    if (result.success) {
      const message = `The synchronization has successfully been scheduled`;
      successMessage(message);
      onSuccess(result.data);
      trackEvent('Synchronization Schedule Created', { schedule: result.data.data });
    } else {
      const message = `There was an error scheduling the synchronization: ${result.response.data.data.message}`;
      errorMessage(message);
    }
  };

  const updateSynchronizationSchedule = async (schedule, startDate, frequency, synchronizables) => {
    const updatedSchedule = {
      startDate,
      frequency,
      synchronizables
    };
    const result = await withLoader(services.replaceSynchronizationSchedule(kgId, schedule.data.id, updatedSchedule));
    if (result.success) {
      const message = `The scheduled synchronization has successfully been updated`;
      successMessage(message);
      onSuccess(result.data);
      trackEvent('Synchronization Schedule Updated', { schedule: result.data.data });
    } else {
      const message = `There was an error updating the scheduled synchronization: ${result.response.data.data.message}`;
      errorMessage(message);
    }
  };

  const onSubmit = data => {
    const startDate = data.startDate.getTime();
    const frequency = data.frequency;
    const synchronizables = data.synchronizables;
    if (schedule) {
      updateSynchronizationSchedule(schedule, startDate, frequency, synchronizables);
    } else {
      createSynchronizationSchedule(startDate, frequency, synchronizables);
    }
  };

  // triggers the validation whenever the schedule to edit changes (in particular initially)
  useEffect(() => {
    if (schedule) {
      trigger();
    }
  }, [schedule]);

  // loads the synchronizables whenever the knowledge graph changes (in particular initially)
  useEffect(() => {
    if (kgId) {
      loadSynchronizableConcepts();
    }
  }, [kgId]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-test="scheduleForm">
      <div className={classes.formSection}>
        <Typography variant="h3" gutterBottom>
          Start Date and Time
        </Typography>
        <Typography variant="body1" gutterBottom>
          Choose the date and time for the first synchronization.
        </Typography>
        <div className={classes.startDateTime}>
          <Controller
            control={control}
            name="startDate"
            render={({ ref, onChange, ...props }, { invalid }) => (
              <DesktopDatePicker
                className={classes.datePicker}
                label="Start date"
                inputFormat="dd/MM/yyyy"
                disablePast
                inputRef={ref}
                renderInput={params => (
                  <TextField
                    size="small"
                    {...params}
                    error={invalid}
                    helperText={errors.startDate?.message}
                    data-test="scheduleStartDatePicker"
                  />
                )}
                // updates the date's time to the one from the start date (or initial start date if not set yet)
                onChange={date => {
                  const startDate = (!isNaN(data.startDate) && data.startDate) || initialStartDate;
                  date?.setHours(startDate.getHours());
                  date?.setMinutes(startDate.getMinutes(), 0, 0);
                  onChange(date);
                }}
                disabled={loading}
                {...props}
              />
            )}
          />
          <DesktopTimePicker
            className={classes.timePicker}
            label="Start time"
            renderInput={params => <TextField size="small" {...params} error={!!errors.startDate} helperText="" />}
            ampm={false}
            value={data.startDate}
            // update the start date of the form data
            onChange={date => setValue('startDate', date, { shouldValidate: true, shouldDirty: true })}
            disabled={loading}
          />
          <div className={classes.timezone}>{Intl.DateTimeFormat().resolvedOptions().timeZone} timezone</div>
        </div>
      </div>

      <div className={classes.formSection}>
        <Typography variant="h3" gutterBottom>
          Frequency
        </Typography>
        <Typography variant="body1" gutterBottom>
          Choose how often the synchronization should run.
        </Typography>
        <Controller
          control={control}
          name="frequency"
          render={({ value, onChange }, { invalid }) => (
            <Box className={classes.frequencyContainer}>
              <div>
                <Select
                  values={_.compact([
                    'once',
                    'recurring',
                    schedule?.data.frequency === 'custom' ? ['custom', true] : null
                  ])}
                  labels={['once', 'every', 'custom']}
                  value={['hourly', 'daily', 'weekly'].includes(value) ? 'recurring' : value}
                  // set the frequency to 'daily' if the user chooses 'recurring'
                  onChange={event => onChange(event.target.value === 'once' ? 'once' : 'daily')}
                  disabled={loading}
                  error={invalid}
                  description={errors.frequency?.message}
                  data-test="scheduleFrequencySelect"
                />
              </div>
              {['hourly', 'daily', 'weekly'].includes(value) && (
                <div>
                  <Select
                    values={['hourly', 'daily', 'weekly']}
                    labels={['hour', 'day', 'week']}
                    value={value}
                    onChange={onChange}
                    disabled={loading}
                    data-test="scheduleRecurringFrequencySelect"
                  />
                </div>
              )}
              {value === 'custom' && !!schedule && (
                <div>
                  <TextField
                    className={classes.customCron}
                    size="small"
                    helperText="Custom cron"
                    value={schedule.data.customCron}
                    disabled
                  />
                </div>
              )}
            </Box>
          )}
        />
      </div>

      <div className={classes.formSection}>
        <Typography variant="h3" gutterBottom>
          Concepts
        </Typography>
        <Typography variant="body1" gutterBottom>
          Choose the concepts to synchronize.
        </Typography>
        {!synchronizables && <Progress fullSize={false} size={20} />}
        {!!synchronizables && (
          <Controller
            control={control}
            name="synchronizables"
            render={({ onChange, value, ...props }, { invalid }) => (
              <>
                <div>
                  <RadioGroup
                    row
                    value={value === null ? 'all' : 'select'}
                    onChange={() =>
                      setValue('synchronizables', value === null ? [] : null, {
                        shouldValidate: true,
                        shouldDirty: true
                      })
                    }
                  >
                    <FormControlLabel
                      value="select"
                      control={<Radio size="small" color="primary" />}
                      label="Select concepts"
                    />
                    <FormControlLabel
                      value="all"
                      control={<Radio size="small" color="primary" />}
                      label={allConceptsText}
                    />
                  </RadioGroup>
                </div>
                {value !== null && (
                  <div className={classes.synchronizablesContainer}>
                    <Autocomplete
                      multiple
                      options={synchronizables}
                      getOptionLabel={getLabel}
                      isOptionEqualToValue={(synchronizable, valueSynchronizable) =>
                        synchronizable.uri === valueSynchronizable.uri
                      }
                      getOptionDisabled={synchronizable => synchronizable.syncPolicy === syncPolicies.ignore}
                      filterSelectedOptions
                      renderInput={params => (
                        <TextField
                          {...params}
                          size="small"
                          label="Concepts"
                          error={invalid}
                          helperText={errors.synchronizables?.message}
                        />
                      )}
                      renderTags={(valueSynchronizables, getTagProps) =>
                        valueSynchronizables.map((synchronizable, index) => (
                          <Synchronizable
                            key={synchronizable.uri}
                            synchronizable={synchronizable}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      value={value || []}
                      onChange={(event, value) => onChange(value)}
                      disabled={loading || value === null}
                      {...props}
                    />
                    <div>
                      <Button
                        variant="outlined"
                        color="primary"
                        className={classes.selectAllButton}
                        disabled={loading || value === null}
                        onClick={() =>
                          setValue('synchronizables', synchronizables, { shouldValidate: true, shouldDirty: true })
                        }
                      >
                        Select all
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          />
        )}
      </div>
      <div className={classes.actions}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!formState.isDirty || !formState.isValid || loading}
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default withStyles(styles)(ScheduleForm);
